/**
 * Resolves which nav entries the current user/company may see. Reads the
 * session bootstrap (`useMe`) — the entitlement + role source identified in
 * Step 0 — and exposes simple predicates the `Sidebar` filters with.
 *
 * Fail-closed: while `/me` is in flight, gated entries stay hidden.
 */
import { useMe } from "@/lib/me";

import type { ModuleKey, NavLeaf, NavSubgroup } from "./nav-config";

export interface NavVisibility {
  enabledModules: string[];
  isSuperAdmin: boolean;
  isApprover: boolean;
  hasModule: (m?: ModuleKey) => boolean;
  canSeeLeaf: (leaf: NavLeaf) => boolean;
  canSeeSubgroup: (group: NavSubgroup) => boolean;
}

export function useNavVisibility(): NavVisibility {
  const { data } = useMe();
  const enabledModules = data?.enabled_modules ?? [];
  const isSuperAdmin = Boolean(data?.is_super_admin || data?.is_superuser);
  // No reliable "is approver" signal exists yet; Approvals stays visible for
  // everyone (the gate plumbing is in place for when that data lands).
  const isApprover = true;

  const hasModule = (m?: ModuleKey) => !m || enabledModules.includes(m);

  const canSeeLeaf = (leaf: NavLeaf) =>
    hasModule(leaf.requiresModule) &&
    (!leaf.requiresSuperAdmin || isSuperAdmin) &&
    (!leaf.requiresApprover || isApprover);

  const canSeeSubgroup = (group: NavSubgroup) => hasModule(group.requiresModule);

  return {
    enabledModules,
    isSuperAdmin,
    isApprover,
    hasModule,
    canSeeLeaf,
    canSeeSubgroup,
  };
}
